<script setup lang="ts">
import type { DistributionGroupWithID, EmissionType } from '#design-tokenomics/types'
import type { ColumnDef } from '@forgd/ui-kit/blocks/data-table'
import { ColumnHeader, DataTable, InputCell } from '@forgd/ui-kit/blocks/data-table'
import { NoData } from '@forgd/ui-kit/blocks/empty-states'
import { Button } from '@forgd/ui-kit/button'
import { Input } from '@forgd/ui-kit/input'
import { InputGroup, InputGroupAddon } from '@forgd/ui-kit/input-group'
import { NumberField, NumberFieldInput } from '@forgd/ui-kit/number-field'
import { Tooltip, TooltipContent, TooltipTrigger } from '@forgd/ui-kit/tooltip'
import { ChevronUpDownIcon } from '@heroicons/vue/24/outline'

defineProps<{
  groups: Array<DistributionGroupWithID>
}>()

const emit = defineEmits<{
  moveGroup: [id: string, type: EmissionType]
}>()

// Full initial unlock has no schedule to edit: the whole allocation is released
// at TGE. The row mirrors the lock & vest columns with the values that implies.
function fixedColumn(id: string, label: string, value: number | string, suffix?: string, tooltip?: string): ColumnDef<DistributionGroupWithID> {
  return {
    id,
    size: 155,
    maxSize: 155,
    header: ({ column }) => h(ColumnHeader, { column, label, class: 'px-1', tooltip }),
    cell: ({ column }) => h(InputCell, { column }, () => {
      if (typeof value !== 'number') {
        return h(Input, { 'modelValue': value, 'size': 'sm', 'disabled': true, 'aria-label': label })
      }

      const precision = suffix === '%' ? 2 : 0
      return h(NumberField, {
        modelValue: value,
        formatOptions: { minimumFractionDigits: precision, maximumFractionDigits: precision },
        disabled: true,
      }, () => h(InputGroup, { size: 'sm' }, () => [
        h(NumberFieldInput, { 'aria-label': label }),
        suffix ? h(InputGroupAddon, null, () => suffix) : null,
      ]))
    }),
  }
}

const columns: ColumnDef<DistributionGroupWithID>[] = [
  {
    accessorKey: 'name',
    size: 200,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Group Name/Allocation %', class: 'pr-1 pl-2', tooltip: 'The groups and their allocations have been defined in Token Distribution. To change them, head back there.' }),
    cell: ({ row, column }) => h(InputCell, { column, class: 'flex items-center gap-2' }, () => [
      h('div', { class: 'h-10 w-1 rounded-full', style: `background-color: ${row.original.color}` }),
      h('div', {}, [
        h('div', {}, row.original.name),
        h('div', { class: 'text-xs font-semibold' }, `${row.original.allocationPercentage}%`),
      ]),
    ]),
  },
  fixedColumn('unlockAtTgePercentage', '% Unlock at TGE', 100, '%', 'The share of this group\'s allocation that unlocks immediately at TGE.'),
  fixedColumn('lockupDurationInMonths', 'Lock Up Duration', 0, 'Months'),
  fixedColumn('unlockAtInitialCliffPercentage', 'Unlock at Initial Cliff', 100, '%', 'The share of this group\'s allocation that unlocks once the lock up period ends, before the remaining tokens start vesting.'),
  fixedColumn('unlockDurationInMonths', 'Unlock Duration', 0, 'Months'),
  fixedColumn('unlockFrequency', 'Unlock Frequency', 'N/A'),
  {
    id: 'move',
    size: 44,
    maxSize: 44,
    header: '',
    cell: ({ row, column }) => h(InputCell, { column }, () => h(Tooltip, () => [
      h(TooltipTrigger, { asChild: true }, () => h(Button, {
        'variant': 'outline',
        'size': 'sm',
        'iconOnly': true,
        'aria-label': 'Move this group to another emission schedule',
        'onClick': () => emit('moveGroup', row.original.id, 'full-initial-unlock'),
      }, () => h(ChevronUpDownIcon, { 'aria-hidden': 'true' }))),
      h(TooltipContent, () => 'Move this group to another emission schedule'),
    ])),
  },
]
</script>

<template>
  <DataTable :data="groups" :columns="columns" size="md">
    <template #empty>
      <NoData message="No groups assigned to this emission type" />
    </template>
  </DataTable>
</template>
